import { useEffect, useState } from "react";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Activity, AlertCircle, CheckCircle2, Clock, RefreshCw } from "lucide-react";
import { GlobalSupabaseStatus } from "@/components/super-admin/GlobalSupabaseStatus";
import { PushNotificationHealth } from "@/components/super-admin/PushNotificationHealth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useTranslation } from "react-i18next";

interface HealthCheck {
  id: string;
  check_type: string;
  status: 'healthy' | 'degraded' | 'down';
  response_time_ms: number;
  error_message?: string;
  checked_at: string;
}

interface SystemAlert {
  id: string;
  alert_type: string;
  severity: string;
  title: string;
  message: string;
  affected_component: string;
  resolved_at: string | null;
  created_at: string;
}

export default function Health() {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["superadmin2", "common"]);

  // Fetch latest health checks
  const { data: healthChecks, isLoading: healthLoading } = useQuery({
    queryKey: ['health-checks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_health_checks')
        .select('*')
        .order('checked_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data as HealthCheck[];
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Check if monitoring is stale (no reports for >10 minutes)
  const isMonitoringStale = healthChecks && healthChecks.length > 0
    ? new Date().getTime() - new Date(healthChecks[0].checked_at).getTime() > 10 * 60 * 1000
    : false;

  // Fetch active alerts
  const { data: activeAlerts, isLoading: alertsLoading } = useQuery({
    queryKey: ['active-alerts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_alerts')
        .select('*')
        .is('resolved_at', null)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as SystemAlert[];
    },
    refetchInterval: 30000,
  });

  // Fetch incident history
  const { data: incidentHistory } = useQuery({
    queryKey: ['incident-history'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_alerts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      return data as SystemAlert[];
    },
  });

  // Manual refresh mutation
  const refreshMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke('health-check-monitor', {
        body: { manual: true },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("health.refreshedToast"));
      queryClient.invalidateQueries({ queryKey: ['health-checks'] });
    },
    onError: (error) => {
      toast.error(t("health.refreshFailedToast", { message: error.message }));
    },
  });

  // Get latest status for each component
  const getLatestStatus = (componentType: string) => {
    if (!healthChecks) return null;
    return healthChecks.find(check => check.check_type === componentType);
  };

  const components = ['database', 'auth', 'storage', 'realtime'];

  // Prepare chart data
  const chartData = healthChecks
    ?.slice(0, 50)
    .reverse()
    .map(check => ({
      time: new Date(check.checked_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      [check.check_type]: check.response_time_ms,
    }))
    .reduce((acc, curr) => {
      const existing = acc.find(item => item.time === curr.time);
      if (existing) {
        Object.assign(existing, curr);
      } else {
        acc.push(curr);
      }
      return acc;
    }, [] as any[]) || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-500';
      case 'degraded': return 'text-yellow-500';
      case 'down': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'degraded': return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'down': return <AlertCircle className="w-5 h-5 text-red-500" />;
      default: return <Activity className="w-5 h-5 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'warning': return 'default';
      case 'info': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{t("health.title")}</h1>
            <p className="text-muted-foreground">{t("health.subtitle")}</p>
          </div>
          <Button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
            {t("health.refresh")}
          </Button>
        </div>

        {/* Monitoring Stale Alert */}
        {isMonitoringStale && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("health.monitoringOfflineTitle")}</AlertTitle>
            <AlertDescription>
              {t("health.monitoringOfflineDesc")}
            </AlertDescription>
          </Alert>
        )}

        {/* Global Supabase Status */}
        <GlobalSupabaseStatus />

        {/* Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {components.map((component) => {
            const status = getLatestStatus(component);
            return (
              <Card key={component} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  {getStatusIcon(status?.status || 'unknown')}
                  <Badge variant={status?.status === 'healthy' ? 'default' : 'destructive'}>
                    {status?.status || 'unknown'}
                  </Badge>
                </div>
                <h3 className="font-semibold capitalize">{component.replace('_', ' ')}</h3>
                {status && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {status.response_time_ms}ms
                  </p>
                )}
              </Card>
            );
          })}
        </div>

        {/* Active Alerts */}
        {activeAlerts && activeAlerts.length > 0 && (
          <Card className="p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              {t("health.activeAlerts", { count: activeAlerts.length })}
            </h2>
            <div className="space-y-3">
              {activeAlerts.map((alert) => (
                <div key={alert.id} className="flex items-start justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={getSeverityColor(alert.severity) as any}>
                        {alert.severity}
                      </Badge>
                      <span className="font-semibold">{alert.title}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">{alert.message}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(alert.created_at).toLocaleString('fr-FR')}
                      </span>
                      <span>{t("health.duration", { minutes: Math.floor((Date.now() - new Date(alert.created_at).getTime()) / 60000) })}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Response Time Chart */}
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">{t("health.responseTimeChart")}</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis label={{ value: t('health.responseTimeAxis'), angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Legend />
              {components.map((component, index) => (
                <Line
                  key={component}
                  type="monotone"
                  dataKey={component}
                  stroke={['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'][index]}
                  name={component.replace('_', ' ')}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Incident History */}
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">{t("health.incidentHistory")}</h2>
          <div className="space-y-2">
            {incidentHistory?.map((incident) => (
              <div key={incident.id} className="flex items-center justify-between p-3 border rounded">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant={getSeverityColor(incident.severity) as any}>
                      {incident.severity}
                    </Badge>
                    <span className="font-medium">{incident.affected_component}</span>
                    {incident.resolved_at && (
                      <Badge variant="outline" className="text-green-600">{t("health.resolved")}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{incident.message}</p>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                  <div>{new Date(incident.created_at).toLocaleString('fr-FR')}</div>
                  {incident.resolved_at && (
                    <div className="text-xs">
                      {t("health.duration", { minutes: Math.floor(
                        (new Date(incident.resolved_at).getTime() - new Date(incident.created_at).getTime()) / 60000
                      ) })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <PushNotificationHealth />
      </div>
    </SuperAdminLayout>

  );
}
